<template>
    <div class="seedance-model-page">
        <!-- Video Generator Section -->
        <section ref="generatorSection" class="video-generator-section">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <h1>{{ t('seedance.generatorTitle') }}</h1>
                            <p class="generator-description">
                                {{ t('seedance.generatorDescription')
                                }}<span class="highlight-red">{{
                                    t('seedance.generatorDescriptionHighlight1')
                                    }}</span>{{ t('seedance.generatorDescriptionText2')
                                    }}<span class="highlight-red">{{
                                    t('seedance.generatorDescriptionHighlight2')
                                    }}</span>{{ t('seedance.generatorDescriptionText3')
                                    }}<span class="highlight-red">{{
                                    t('seedance.generatorDescriptionHighlight3')
                                    }}</span>{{ t('seedance.generatorDescriptionText4')
                                    }}<span class="highlight-red">{{
                                    t('seedance.generatorDescriptionHighlight4')
                                    }}</span>{{ t('seedance.generatorDescriptionEnd') }}
                            </p>
                            <div class="rainbow-bar"></div>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <VideoGenerator model-name="Seedance" :tabs="tabs" redirect-url="/ai-video"
                        @generate="handleGenerate" model-key="Seedance-1.0">
                        <template #model-logo>
                            <div class="seedance-logo">S</div>
                        </template>
                    </VideoGenerator>
                </div>
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedance.coreFeaturesTitle') }}
                    </h2>
                </div>

                <div class="features-showcase-container">
                    <!-- Accurate Semantic Understanding Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('seedance.accurateSemanticTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'seedance.accurateSemanticDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.accurateSemantic"
                                    :poster="getResourceThumbnail(demoVideos.accurateSemantic)"
                                    :title="t('seedance.accurateSemanticTitle')"
                                    :description="
                                        t(
                                            'seedance.accurateSemanticDescription'
                                        )
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Dynamic Camera Control Feature -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.dynamicCameraControl"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.dynamicCameraControl
                                        )
                                    "
                                    :title="
                                        t('seedance.dynamicCameraControlTitle')
                                    "
                                    :description="
                                        t(
                                            'seedance.dynamicCameraControlDescription'
                                        )
                                    "
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('seedance.dynamicCameraControlTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'seedance.dynamicCameraControlDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Multi-Shot Generation with Consistency Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('seedance.multiShotGenerationTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'seedance.multiShotGenerationDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.multiShotGeneration"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.multiShotGeneration
                                        )
                                    "
                                    :title="
                                        t('seedance.multiShotGenerationTitle')
                                    "
                                    :description="
                                        t(
                                            'seedance.multiShotGenerationDescription'
                                        )
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Support for Multiple Styles Feature -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.multipleStyles"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.multipleStyles
                                        )
                                    "
                                    :title="t('seedance.multipleStylesTitle')"
                                    :description="
                                        t(
                                            'seedance.multipleStylesDescription'
                                        )
                                    "
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('seedance.multipleStylesTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t('seedance.multipleStylesDescription')
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedance.howToUseTitle') }}
                    </h2>
                </div>

                <div class="steps-container">
                    <div class="step-item">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('seedance.step1Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('seedance.step1Description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('seedance.step2Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('seedance.step2Description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('seedance.step3Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('seedance.step3Description') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <section class="youtube-videos-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedance.youtubeTitle') }}
                    </h2>
                </div>

                <div class="youtube-carousel">
                    <button class="carousel-nav-btn carousel-nav-left" @click="previousVideo"
                        :aria-label="t('common.previous')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>

                    <div class="videos-wrapper">
                        <div class="videos-scroll" :style="{
                            transform: `translateX(-${currentVideoIndex * (100 / 3)}%)`,
                        }">
                            <div v-for="(video, index) in youtubeVideos" :key="index" class="video-iframe-container">
                                <iframe :src="video.embedUrl" :title="video.title" class="video-iframe" frameborder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowfullscreen></iframe>
                            </div>
                        </div>
                    </div>

                    <button class="carousel-nav-btn carousel-nav-right" @click="nextVideo"
                        :aria-label="t('common.next')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </section>

        <!-- X Posts Section -->
        <TwitterPosts :title="t('seedance.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedance.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/video/seedance-pro-fast" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/98653aa87810721ed41e47d1df57b068.png"
                                :alt="t('seedance.seedanceProFastTitle')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedance.seedanceProFastTitle') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/sora/sora-2" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png"
                                :alt="t('seedance.sora2Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedance.sora2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/runway-ai" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/a65ca4242d4a0dc3f1b65e1d96471144.png"
                                :alt="t('seedance.runwayTitle')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedance.runwayTitle') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('seedance.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <!-- <img src="/weryai_48.png" alt="微鲤AI" class="logo-image" /> -->
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI logo" class="logo-image" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('seedance.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('seedance.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('seedance.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('seedance.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import { getResourceThumbnail } from '~/utils/common'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

const demoVideos = {
    accurateSemantic:
        'https://static.weryai.com/growth/video/4a26c543e4675380ca5fbcd7d6797043.mp4',
    dynamicCameraControl:
        'https://static.weryai.com/growth/video/4ed53bd362a9c9a611de8cdfcb9d7547.mp4',
    multiShotGeneration:
        'https://static.weryai.com/growth/video/6dd5fcf50ddb0610546bd4ccf7e206bd.mp4',
    multipleStyles:
        'https://static.weryai.com/growth/video/95ed38b1015ca60232e1477321e8cf97.mp4',
} as const

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'seedance' })

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('seedance.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'seedance_1_0',
        type: 'video',
    })
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// Structured Data for SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: t('seedance.generatorTitle'),
                description: t('seedance.pageDescription'),
                applicationCategory: 'MultimediaApplication',
                operatingSystem: 'Web',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
                provider: {
                    '@type': 'Organization',
                    name: '微鲤AI',
                    url: 'https://www.weryai.com.cn',
                },
                featureList: [
                    t('seedance.accurateSemanticTitle'),
                    t('seedance.dynamicCameraControlTitle'),
                    t('seedance.multiShotGenerationTitle'),
                    t('seedance.multipleStylesTitle'),
                ],
            }),
        },
    ],
})

// Tabs configuration
const tabs = computed(() => [
    { name: t('seedance.tabImageToVideo'), external: false },
    { name: t('seedance.tabTextToVideo'), external: false },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // 组件内部已经处理了跳转逻辑
}

// HowTo Steps for SEO
const howToUseSteps = computed(() => [
    {
        title: t('seedance.step1Title'),
        description: t('seedance.step1Description'),
    },
    {
        title: t('seedance.step2Title'),
        description: t('seedance.step2Description'),
    },
    {
        title: t('seedance.step3Title'),
        description: t('seedance.step3Description'),
    },
])

// HowTo SEO - 注入结构化数据
useHowToSeo(
    computed(() => t('seedance.howToUseTitle')),
    howToUseSteps
)

// YouTube Videos Data
const currentVideoIndex = ref(0)

const youtubeVideos = ref([
    {
        title: 'Seedance Video 1',
        embedUrl: 'https://www.youtube.com/embed/Y4AbT6AopBM',
    },
    {
        title: 'Seedance Video 2',
        embedUrl: 'https://www.youtube.com/embed/2zDeXGfmWR0',
    },
    {
        title: 'Seedance Video 3',
        embedUrl: 'https://www.youtube.com/embed/qq8TujV5xMI',
    },
    {
        title: 'Seedance Video 4',
        embedUrl: 'https://www.youtube.com/embed/16oB2xYjM7Y',
    },
])

// Video SEO - 注入 VideoObject 结构化数据
useVideoSeo(youtubeVideos.value)

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://twitter.com/HBCoop_/status/1949880169910395357',
    'https://twitter.com/ozan_sihay/status/1932038287885000915',
    'https://twitter.com/Machinedelusion/status/1949869887813276061',
    'https://twitter.com/sankakuten91256/status/1949333022777770385',
    'https://twitter.com/el_el_san/status/1949674076739662281',
    'https://twitter.com/ImgMotion/status/1949697269785120968',
    'https://twitter.com/opener_ai/status/1949978375453376857',
    'https://twitter.com/TheJasonRink/status/1949592127182115289',
    'https://twitter.com/DesigningFlow/status/1949419531899711974',
]

// Carousel Methods
const nextVideo = () => {
    // Maximum 3 visible videos, so max index is 2 (3rd video)
    const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
    if (currentVideoIndex.value >= maxVisibleIndex) {
        currentVideoIndex.value = 0 // Back to first video
    } else {
        currentVideoIndex.value++
    }
}

const previousVideo = () => {
    if (currentVideoIndex.value <= 0) {
        // Back to last visible video position
        const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
        currentVideoIndex.value = maxVisibleIndex
    } else {
        currentVideoIndex.value--
    }
}

// FAQ Data
const faqData = computed(() => [
    {
        question: t('seedance.faq1Question'),
        answer: t('seedance.faq1Answer'),
    },
    {
        question: t('seedance.faq2Question'),
        answer: t('seedance.faq2Answer'),
    },
    {
        question: t('seedance.faq3Question'),
        answer: t('seedance.faq3Answer'),
    },
    {
        question: t('seedance.faq4Question'),
        answer: t('seedance.faq4Answer'),
    },
    {
        question: t('seedance.faq5Question'),
        answer: t('seedance.faq5Answer'),
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

// Video 交互功能
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
